import React, { useState, useEffect } from 'react';
import { Trash2, Mail, Calendar, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { dpoContactAPI } from '../../services/api';

const DPOContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await dpoContactAPI.getAll();
      setContacts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei contatti DPO');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contatto?')) return;
    try {
      await dpoContactAPI.delete(id);
      toast.success('Contatto eliminato!');
      loadContacts();
    } catch (error) {
      toast.error('Errore nell\'eliminare il contatto');
    }
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <CardTitle className="text-2xl">Contatti DPO</CardTitle>
            <CardDescription>Richieste inviate al Responsabile Protezione Dati</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Oggetto</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8">Caricamento...</TableCell></TableRow>
              ) : contacts.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nessun contatto DPO</TableCell></TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.id} data-testid={`dpo-contact-row-${contact.id}`}>
                    <TableCell className="font-medium">{contact.name}</TableCell>
                    <TableCell>
                      <a href={`mailto:${contact.email}`} className="text-primary hover:underline flex items-center">
                        <Mail className="w-4 h-4 mr-1" />
                        {contact.email}
                      </a>
                    </TableCell>
                    <TableCell>{contact.subject}</TableCell>
                    <TableCell>
                      <div className="flex items-center text-muted-foreground">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(contact.created_at).toLocaleDateString('it-IT')}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSelectedContact(contact)}
                          data-testid={`view-dpo-${contact.id}`}
                        >
                          Visualizza
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(contact.id)} 
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-dpo-${contact.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Detail Dialog */}
        <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Dettaglio Richiesta DPO</DialogTitle>
              <DialogDescription>Informazioni complete sulla richiesta</DialogDescription>
            </DialogHeader>
            {selectedContact && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Nome</p>
                    <p className="font-medium">{selectedContact.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedContact.email}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Oggetto</p>
                  <p className="font-medium">{selectedContact.subject}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Messaggio</p>
                  <div className="mt-2 p-4 rounded-lg bg-muted">
                    <p className="whitespace-pre-wrap">{selectedContact.message}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data ricezione</p>
                  <p className="font-medium">{new Date(selectedContact.created_at).toLocaleString('it-IT')}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default DPOContactsTab;
